import { apiConfig } from '@/config';
import { useApi } from '@/hooks/useApi';
import type { AuthStatus } from '@/types/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

interface AuthStatusWithDebug extends AuthStatus {
  debug_error?: string;
  metadata?: Record<string, unknown>;
}

function getNextUtcMidnight(nowMs: number): Date {
  const now = new Date(nowMs);
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0));
}

export function useUserLimits() {
  const api = useApi();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['userLimits'],
    queryFn: async () => {
      const { data } = await api.get<AuthStatusWithDebug>(apiConfig.endpoints.authStatus);
      return data.limits;
    },
    refetchInterval: 60000, // Refetch every minute
    refetchIntervalInBackground: true, // Keep credits fresh across UTC midnight even if tab is backgrounded
    staleTime: 30000,
  });

  useEffect(() => {
    let timeoutId: number | undefined;

    const scheduleMidnightInvalidate = () => {
      const nowMs = Date.now();
      const nextResetAt = getNextUtcMidnight(nowMs);
      const delayMs = Math.max(0, nextResetAt.getTime() - nowMs + 1000);

      timeoutId = window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['userLimits'] });
        scheduleMidnightInvalidate();
      }, delayMs);
    };

    scheduleMidnightInvalidate();

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [queryClient]);

  return query;
}
